import { useCallback, useState } from 'react';

export type KBItemType = 'folder' | 'document' | 'page';

export interface KBSelectedItem {
  id: string;
  type: KBItemType;
}

export function useKBSelection() {
  const [selectedItems, setSelectedItems] = useState<Map<string, KBItemType>>(
    new Map(),
  );
  const [cutItems, setCutItems] = useState<Map<string, KBItemType> | null>(
    null,
  );
  const [cutSourceFolderId, setCutSourceFolderId] = useState<string | null>(
    null,
  );

  const selectionType = (() => {
    if (selectedItems.size === 0) return null;

    const types = new Set(selectedItems.values());

    if (types.size > 1) return 'mixed' as const;

    return types.values().next().value ?? null;
  })();

  const toggleItem = useCallback(
    (id: string, type: KBItemType, multi: boolean) => {
      setSelectedItems(prev => {
        // Single-click on already-selected item → deselect
        if (!multi && prev.size === 1 && prev.has(id)) {
          return new Map();
        }

        const next = multi ? new Map(prev) : new Map();

        if (next.has(id)) {
          next.delete(id);
        } else {
          next.set(id, type);
        }

        return next;
      });
    },
    [],
  );

  const setItemSelected = useCallback(
    (id: string, type: KBItemType, selected: boolean) => {
      setSelectedItems(prev => {
        const next = new Map(prev);

        if (selected) {
          next.set(id, type);
        } else {
          next.delete(id);
        }

        return next;
      });
    },
    [],
  );

  const selectRange = useCallback((items: KBSelectedItem[]) => {
    setSelectedItems(prev => {
      const next = new Map(prev);

      for (const item of items) {
        next.set(item.id, item.type);
      }

      return next;
    });
  }, []);

  const selectAll = useCallback((items: KBSelectedItem[]) => {
    const next = new Map<string, KBItemType>();

    for (const item of items) {
      next.set(item.id, item.type);
    }

    setSelectedItems(next);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedItems(new Map());
  }, []);

  const toggleSelectAll = useCallback((items: KBSelectedItem[]) => {
    setSelectedItems(prev => {
      if (items.length === 0) {
        return new Map();
      }

      const allSelected = items.every(item => prev.has(item.id));

      if (allSelected) {
        return new Map();
      }

      const next = new Map<string, KBItemType>();

      for (const item of items) {
        next.set(item.id, item.type);
      }

      return next;
    });
  }, []);

  const startCut = useCallback(
    (currentFolderId: string | null, itemsToCut?: Map<string, KBItemType>) => {
      setCutItems(new Map(itemsToCut ?? selectedItems));
      setCutSourceFolderId(currentFolderId);
    },
    [selectedItems],
  );

  const cancelCut = useCallback(() => {
    setCutItems(null);
    setCutSourceFolderId(null);
  }, []);

  const canPasteHere = useCallback(
    (currentFolderId: string | null) =>
      cutItems != null &&
      cutItems.size > 0 &&
      currentFolderId !== cutSourceFolderId,
    [cutItems, cutSourceFolderId],
  );

  const completePaste = useCallback(() => {
    setCutItems(null);
    setCutSourceFolderId(null);
    setSelectedItems(new Map());
  }, []);

  return {
    selectedItems,
    selectionType,
    toggleItem,
    setItemSelected,
    selectRange,
    selectAll,
    toggleSelectAll,
    clearSelection,
    cutItems,
    cutSourceFolderId,
    startCut,
    cancelCut,
    canPasteHere,
    completePaste,
  };
}
